import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert array of objects to CSV string with custom headers.
 */
/**
 * M-034 fix: Neutralize CSV formula injection.
 * Values starting with =, +, -, @, \t, \r are prefixed with a single quote
 * to prevent Excel/LibreOffice from interpreting them as formulas.
 */
export function sanitizeCsvValue(value: string): string {
  // Vezető whitespace NEM bypass: Excel trimmeli, és a ` =1+1` is formulaként fut
  // (Lens-1+4 gap, 2026-06-12). Tab/CR-kezdet önmagában is prefixet kap (OWASP-lista).
  if (/^[\t\r]/.test(value) || /^\s*[=+\-@]/.test(value)) {
    return "'" + value;
  }
  return value;
}

export function objectsToCsv<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T & string; header: string }[]
): string {
  const header = columns.map(c => `"${sanitizeCsvValue(c.header)}"`).join(',');
  const rows = data.map(row =>
    columns
      .map(c => {
        const val = row[c.key];
        if (typeof val === 'string') {
          const escaped = val.replace(/"/g, '""').replace(/[\r\n]+/g, ' ');
          return `"${sanitizeCsvValue(escaped)}"`;
        }
        return String(val ?? '');
      })
      .join(',')
  );
  return [header, ...rows].join('\n');
}

/**
 * Format a number as Hungarian Forint (HUF) currency string.
 * Uses hu-HU locale with no fraction digits.
 */
export function formatHuf(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a date string as Hungarian locale date (without time).
 * Output: "2026. április 6."
 */
export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date string as Hungarian locale date+time.
 * Output: "2026. 04. 06. 14:30"
 */
export function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Smart date formatter: shows time only when meaningful (not midnight UTC).
 * Legacy rentals stored as date-only have 00:00 UTC — shown without time.
 * ADR-078 rentals with real times — shown with HH:mm.
 */
export function formatDateSmart(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '–';
  const hasTime = d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0;
  return hasTime ? formatDateTime(dateStr) : formatDate(dateStr);
}

/**
 * 41-kör FE-3 (quick-win ①): kompakt relatív idő friss eseményekre („5 perce",
 * „2 órája", „tegnap"), 7 napon túl abszolút formára vált (formatDateSmart).
 * A hu-locale a fájl többi formatterével konzisztens (mind hu-HU-fix).
 * `nowMs` injektálható a determinista tesztekhez.
 */
export function formatRelativeTime(dateStr: string, nowMs: number = Date.now()): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '–';
  const diffMs = d.getTime() - nowMs;
  const absSec = Math.abs(diffMs) / 1000;
  if (absSec >= 7 * 86400) return formatDateSmart(dateStr);
  // narrow: a kompakt hu-alak („5 perce", „2 órája") — a long a hosszú
  // „perccel ezelőtt" formát adná (measured: CLDR hu long==short)
  const rtf = new Intl.RelativeTimeFormat('hu', { numeric: 'auto', style: 'narrow' });
  if (absSec < 60) return rtf.format(Math.round(diffMs / 1000), 'second');
  if (absSec < 3600) return rtf.format(Math.round(diffMs / 60000), 'minute');
  if (absSec < 86400) return rtf.format(Math.round(diffMs / 3600000), 'hour');
  return rtf.format(Math.round(diffMs / 86400000), 'day');
}

/**
 * Trigger a file download in the browser.
 */
export function downloadAsFile(
  content: string,
  filename: string,
  mimeType = 'text/csv;charset=utf-8;'
): void {
  const blob = new Blob(['\uFEFF' + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}
